import { EventProps } from '../Event/Event.types';

/**
 * Filters out events that have already ended based on their end date/time
 * @param events Array of events to filter
 * @param hideOldEvents Whether to hide old events (defaults to true)
 * @returns Filtered array of events
 */
export const filterOldEvents = (events: EventProps[], hideOldEvents: boolean = true): EventProps[] => {
  if (!hideOldEvents) {
    return events;
  }

  const now = new Date();

  return events.filter((event) => {
    // Use end date if available, otherwise use start date
    const eventEndDate = new Date(event.endDateTime || event.startDateTime);

    // Keep events that haven't ended yet
    return eventEndDate > now;
  });
};

/**
 * Filters events by month and year
 * @param events Array of events to filter
 * @param selectedMonth Month (1-12) or undefined for all months
 * @param selectedYear Year or undefined for all years
 * @returns Filtered array of events
 */
export const filterEventsByDate = (
  events: EventProps[],
  selectedMonth?: number,
  selectedYear?: number
): EventProps[] => {
  if (!selectedMonth && !selectedYear) {
    return events;
  }

  return events.filter((event) => {
    const eventDate = new Date(event.startDateTime);
    const eventMonth = eventDate.getMonth() + 1; // JavaScript months are 0-indexed
    const eventYear = eventDate.getFullYear();

    const monthMatches = !selectedMonth || eventMonth === selectedMonth;
    const yearMatches = !selectedYear || eventYear === selectedYear;

    return monthMatches && yearMatches;
  });
};

/**
 * Filters events by event type
 * @param events Array of events to filter
 * @param selectedEventTypes Array of selected event type IDs
 * @returns Filtered array of events
 */
export const filterEventsByType = (events: EventProps[], selectedEventTypes: string[]): EventProps[] => {
  if (selectedEventTypes.length === 0) {
    return events;
  }

  return events.filter((event) => {
    if (!event.eventType) {
      return false;
    }
    return selectedEventTypes.includes(event.eventType);
  });
};

/**
 * Filters events by audience
 * @param events Array of events to filter
 * @param selectedAudiences Array of selected audience IDs
 * @returns Filtered array of events
 */
export const filterEventsByAudience = (events: EventProps[], selectedAudiences: string[]): EventProps[] => {
  if (selectedAudiences.length === 0) {
    return events;
  }

  return events.filter((event) => {
    if (!event.audience || event.audience.length === 0) {
      return false;
    }

    // Check if any of the event's audiences match the selected audiences
    return event.audience.some(audience => selectedAudiences.includes(audience));
  });
};

/**
 * Filters events by search term (searches in name and description)
 * @param events Array of events to filter
 * @param searchTerm Search term to look for
 * @returns Filtered array of events
 */
export const filterEventsBySearch = (events: EventProps[], searchTerm: string): EventProps[] => {
  if (!searchTerm.trim()) {
    return events;
  }

  const lowercaseSearch = searchTerm.toLowerCase();

  return events.filter((event) => {
    const nameMatch = event.name.toLowerCase().includes(lowercaseSearch);
    const descriptionMatch = event.description.toLowerCase().includes(lowercaseSearch);
    const organizationMatch = event.organization?.name?.toLowerCase().includes(lowercaseSearch) || false;

    return nameMatch || descriptionMatch || organizationMatch;
  });
};

/**
 * Sorts events by date (earliest first)
 * @param events Array of events to sort
 * @returns Sorted array of events
 */
export const sortEventsByDate = (events: EventProps[]): EventProps[] => {
  return [...events].sort((a, b) => {
    const dateA = new Date(a.startDateTime);
    const dateB = new Date(b.startDateTime);
    return dateA.getTime() - dateB.getTime();
  });
};

/**
 * Comprehensive filter function that applies all filters
 * @param events Array of events to filter
 * @param filters Object containing all filter criteria
 * @returns Filtered and sorted array of events
 */
export const applyAllEventFilters = (
  events: EventProps[],
  filters: {
    hideOldEvents?: boolean;
    selectedMonth?: number;
    selectedYear?: number;
    selectedEventTypes?: string[];
    selectedAudiences?: string[];
    searchTerm?: string;
  }
): EventProps[] => {
  let filteredEvents = events;

  // Apply old events filter
  filteredEvents = filterOldEvents(filteredEvents, filters.hideOldEvents);

  // Apply date filter
  filteredEvents = filterEventsByDate(filteredEvents, filters.selectedMonth, filters.selectedYear);

  // Apply event type filter
  if (filters.selectedEventTypes && filters.selectedEventTypes.length > 0) {
    filteredEvents = filterEventsByType(filteredEvents, filters.selectedEventTypes);
  }

  // Apply audience filter
  if (filters.selectedAudiences && filters.selectedAudiences.length > 0) {
    filteredEvents = filterEventsByAudience(filteredEvents, filters.selectedAudiences);
  }

  // Apply search filter
  if (filters.searchTerm) {
    filteredEvents = filterEventsBySearch(filteredEvents, filters.searchTerm);
  }

  // Sort by date
  return sortEventsByDate(filteredEvents);
};